import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, FileText, Briefcase, Users, ArrowRight } from "lucide-react";
import { insights, team, caseStudies } from "../mock";
import { LogoMark } from "./Logo";

const buildIndex = () => {
  const items = [];
  insights.forEach((p) =>
    items.push({
      kind: "insight",
      title: p.title,
      subtitle: `${p.category} · ${p.readTime}`,
      keywords: [p.title, p.excerpt, p.category, ...(p.tags || [])].join(" ").toLowerCase(),
      to: `/insights/${p.slug}`,
    })
  );
  Object.entries(caseStudies).forEach(([service, list]) =>
    list.forEach((c) =>
      items.push({
        kind: "case",
        title: c.title,
        subtitle: `${c.client} · ${service.charAt(0).toUpperCase() + service.slice(1)}`,
        keywords: [c.title, c.client, c.industry, c.challenge, c.approach, service, ...(c.services || [])]
          .join(" ")
          .toLowerCase(),
        to: `/case-studies/${c.slug}`,
      })
    )
  );
  team.forEach((m) =>
    items.push({
      kind: "team",
      title: m.role,
      subtitle: `${m.department} · ${m.location}`,
      keywords: [m.role, m.bio, m.department, m.long, ...(m.expertise || [])].join(" ").toLowerCase(),
      to: `/team/${m.slug}`,
    })
  );
  return items;
};

const kindConfig = {
  insight: { label: "Insight", Icon: FileText, color: "text-blue-600 bg-blue-50 border-blue-100" },
  case: { label: "Case Study", Icon: Briefcase, color: "text-teal-700 bg-teal-50 border-teal-100" },
  team: { label: "Team", Icon: Users, color: "text-amber-700 bg-amber-50 border-amber-100" },
};

const SearchDialog = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const index = useMemo(buildIndex, []);

  const query = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!query) return index.slice(0, 8);
    return index
      .map((i) => ({ ...i, score: i.keywords.includes(query) ? 1 : 0 }))
      .filter((i) => i.score > 0)
      .slice(0, 20);
  }, [query, index]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(a + 1, results.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
      }
      if (e.key === "Enter") {
        const item = results[active];
        if (item) {
          navigate(item.to);
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, results, active, navigate, onClose]);

  if (!open) return null;

  const grouped = results.reduce((acc, r) => {
    acc[r.kind] = acc[r.kind] || [];
    acc[r.kind].push(r);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-neutral-200">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
          <LogoMark size={28} />
          <div className="w-px h-6 bg-neutral-200" />
          <Search className="w-5 h-5 text-neutral-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Search insights, case studies, and team…"
            className="flex-1 bg-transparent outline-none text-neutral-900 placeholder:text-neutral-400"
          />
          <kbd className="hidden sm:inline text-[10px] px-2 py-1 rounded bg-neutral-100 text-neutral-500 border border-neutral-200">
            ESC
          </kbd>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 && (
            <div className="px-6 py-16 text-center text-neutral-500">
              No matches for <span className="font-semibold text-neutral-800">“{q}”</span>
            </div>
          )}
          {["insight", "case", "team"].map((kind) => {
            const items = grouped[kind];
            if (!items || items.length === 0) return null;
            const cfg = kindConfig[kind];
            return (
              <div key={kind} className="py-2">
                <div className="px-5 py-2 text-[10px] font-bold tracking-widest text-neutral-500 uppercase">
                  {cfg.label}
                </div>
                {items.map((r) => {
                  const globalIdx = results.indexOf(r);
                  const isActive = globalIdx === active;
                  const Icon = cfg.Icon;
                  return (
                    <Link
                      key={r.to}
                      to={r.to}
                      onClick={onClose}
                      onMouseEnter={() => setActive(globalIdx)}
                      className={`flex items-center gap-3 px-5 py-3 border-l-2 transition-colors ${
                        isActive ? "bg-teal-50 border-teal-500" : "border-transparent hover:bg-neutral-50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${cfg.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-neutral-900 truncate">{r.title}</div>
                        <div className="text-xs text-neutral-500 truncate">{r.subtitle}</div>
                      </div>
                      <ArrowRight className={`w-4 h-4 ${isActive ? "text-teal-600" : "text-neutral-300"}`} />
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-neutral-100 text-[11px] text-neutral-500 flex items-center gap-4">
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">↵</kbd> open</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default SearchDialog;
